import { useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { useAuth } from '@/hooks/useAuth';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { OAUTH_OUTCOME_KEY } from '@/infrastructure/clients/BackendClient';
import { getSignupAttributionProperties } from '@/modules/attribution/attribution';
import { queueMemberPreferencesWrite } from '@/infrastructure/adapters/memberPreferencesCache';

/**
 * Fires the sign-up / log-in event for OAuth (Google/Apple) returns.
 *
 * OAuth lands in AuthProvider, which sits above ServiceProvider and so can't emit
 * analytics. It leaves a one-shot {provider, isNewUser} marker that this component —
 * inside ServiceProvider, with the analytics service — consumes once the user is known
 * and PostHog is bound. New users (signing in provisioned their org) get
 * Lureo_App_Sign_Up + first-touch attribution; returning users get Lureo_App_Log_In.
 *
 * Without this, the email form was the only sign-up call site, so OAuth signups went
 * entirely untracked. (lureo-docs#256)
 */
export function OAuthAnalyticsTracker() {
  const posthog = usePostHog();
  const { user } = useAuth();
  const services = useServices();

  useEffect(() => {
    // Gate on PostHog being bound (PostHogAnalyticsBinder runs first) so we never
    // consume the one-shot marker before the event can actually be sent.
    if (!posthog || !user?.id) return;

    let outcome: { provider?: string; isNewUser?: boolean; marketingConsent?: boolean } | null;
    try {
      const raw = sessionStorage.getItem(OAUTH_OUTCOME_KEY);
      if (!raw) return;
      outcome = JSON.parse(raw);
      sessionStorage.removeItem(OAUTH_OUTCOME_KEY);
    } catch {
      return;
    }
    if (!outcome) return;

    const provider = outcome.provider || 'oauth';
    const marketingConsent = outcome.marketingConsent === true;
    services.analytics.identify(user.id);
    if (outcome.isNewUser) {
      const attribution = getSignupAttributionProperties();
      services.analytics.setPersonProperties(attribution);
      services.analytics.trackLureoAppSignUp({ signup_method: provider, ...attribution });

      // The backend provisions OAuth users with marketing_consent=false. If the user
      // opted in before the redirect, apply it now — isNewUser is only true after the
      // org_member has been provisioned, so this update always finds the record.
      // updateMarketingConsent reports its own failures to Sentry.
      const consentSaved = marketingConsent
        ? queueMemberPreferencesWrite(user.id, () => services.auth.updateMarketingConsent({ marketingConsent: true })).catch(() => {})
        : Promise.resolve();

      // Notify the Slack signup channel, matching the email path. The backend reads the
      // name, provider and marketing consent from the account, so it waits for the consent.
      // Provider is known for real OAuth returns; skip Slack on the lost-marker fallback.
      // Fire-and-forget. (lureo-docs#256)
      if (provider === 'google' || provider === 'apple') {
        void consentSaved.then(() => services.auth.notifySlackUserSignup());
      }
    } else {
      services.analytics.trackLureoAppLogIn({ login_method: provider });
    }
  }, [posthog, user?.id, services]);

  return null;
}
